import EmptyState from '@components/Common/EmptyState';
import FilterRow from '@components/Common/FilterRow';
import MetricTable, { type MetricColumn } from '@components/Common/MetricTable';
import Panel from '@components/Common/Panel';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { getSignupAttributionQuery } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  type Audience,
  type SignupWeek,
  type WeeklyRow,
  countFor,
  totalRow,
  weekLabel,
  weeklyRows,
} from './signupWeeks';
import './index.scss';

export const WEEK_RANGES = [4, 12, 26];
const AUDIENCES: { value: Audience; label: string }[] = [
  { value: 'jobSeekers', label: 'Job seekers' },
  { value: 'employers', label: 'Employers' },
  { value: 'both', label: 'Both' },
];
/** Channels are free-form (utm source / medium), so the tail is long; past six it is noise. */
const CHANNEL_LIMIT = 6;

const num = (n: number) => n.toLocaleString('en-IN');

const columnsFor = (weeks: SignupWeek[], first: string): MetricColumn<WeeklyRow>[] => [
  {
    key: 'key',
    header: first,
    variant: 'name',
    render: (r) => (r.summary ? <span className="sw-total">{r.key}</span> : r.key),
  },
  ...weeks.map((w, i) => {
    const { label, note } = weekLabel(w.weekStart, i === weeks.length - 1);
    return {
      key: w.weekStart,
      header: (
        <span className="sw-week">
          <span>{label}</span>
          {note && <span className="sw-week-note">{note}</span>}
        </span>
      ),
      tooltip: `Week starting ${label}${note ? ', still in progress' : ''}`,
      render: (r: WeeklyRow) => (r.summary ? <span className="sw-total">{num(r.values[i])}</span> : num(r.values[i])),
    };
  }),
  {
    key: 'total',
    header: 'Total',
    render: (r) => <span className="sw-total">{num(r.total)}</span>,
  },
];

/**
 * One weeks-across table. It opens scrolled to its right edge: the newest week is on the right,
 * and when the weeks do not fit (a phone, or 26 weeks on any screen) that is the column that
 * matters. The name column is pinned at every width so scrolling never loses the row.
 */
const WeeklyTable: React.FC<{ title: string; first: string; rows: WeeklyRow[]; weeks: SignupWeek[]; total: WeeklyRow }> = ({
  title,
  first,
  rows,
  weeks,
  total,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const scroller = ref.current?.querySelector<HTMLElement>('.mt-scroll');
    if (scroller) scroller.scrollLeft = scroller.scrollWidth;
  }, [weeks.length]);

  return (
    <Box className="sw-table" ref={ref}>
      <Typography className="sw-table-title" variant="subtitle2">{title}</Typography>
      <MetricTable<WeeklyRow>
        rows={rows.length ? [...rows, total] : []}
        getRowKey={(r) => (r.summary ? '__total' : r.key)}
        columns={columnsFor(weeks, first)}
        emptyLabel="No sign-ups in these weeks."
        pinName
      />
    </Box>
  );
};

/**
 * Sign-ups by week: the same self sign-ups as the section above, one column per week, so a change
 * in where people come from shows up as a column rather than having to be remembered between
 * visits. The daily chart answers "how many"; this answers "from where, and is it moving".
 *
 * The query is newer than the daily one and ships from its own backend branch. Until that is
 * deployed it errors, and this panel says so in one line instead of taking the dashboard down.
 */
const SignupsByWeek: React.FC = () => {
  const [weeks, setWeeks] = useState(12);
  const [audience, setAudience] = useState<Audience>('jobSeekers');
  const [data, setData] = useState<SignupWeek[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    setError(false);
    setData(null);
    fetchGraphQLApiData(getSignupAttributionQuery(weeks))
      .then((r) => {
        if (!live) return;
        const list = r?.data?.getSignupAttribution?.weeks;
        if (Array.isArray(list)) setData(list.filter(Boolean));
        else setError(true);
      })
      .catch(() => { if (live) setError(true); });
    return () => { live = false; };
  }, [weeks]);

  const tables = useMemo(() => {
    if (!data) return null;
    const total = totalRow(data, audience);
    return {
      total,
      channels: weeklyRows(data, 'channels', audience, CHANNEL_LIMIT),
      landingPages: weeklyRows(data, 'landingPages', audience),
      methods: weeklyRows(data, 'methods', audience),
    };
  }, [data, audience]);

  const signedUp = data ? data.reduce((s, w) => s + countFor(w, audience), 0) : 0;

  return (
    <Panel className="db-section sw-section">
      <Box className="db-section-head">
        <Box className="db-section-header">
          <Typography className="db-section-title" variant="subtitle1">Sign-ups by week</Typography>
          <Typography className="db-section-sub" variant="body2">
            Where people came from and where they landed, week by week. Weeks start on Monday, IST.
          </Typography>
        </Box>
      </Box>

      <FilterRow>
        <ToggleButtonGroup
          value={weeks}
          exclusive
          size="small"
          aria-label="Weeks shown"
          onChange={(_, v) => { if (v !== null) setWeeks(v); }}
        >
          {WEEK_RANGES.map((w) => (
            <ToggleButton key={w} value={w}>{`${w} weeks`}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <ToggleButtonGroup
          value={audience}
          exclusive
          size="small"
          aria-label="Who to count"
          onChange={(_, v) => { if (v !== null) setAudience(v); }}
        >
          {AUDIENCES.map((a) => (
            <ToggleButton key={a.value} value={a.value}>{a.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </FilterRow>

      {error ? (
        <Box className="sw-notice" role="status">
          <InfoOutlinedIcon className="sw-notice-icon" />
          <Typography variant="body2" className="sw-notice-text">
            Weekly sign-up attribution is not available yet. The daily view above is unaffected.
          </Typography>
        </Box>
      ) : !data || !tables ? (
        <SkeletonLoader />
      ) : signedUp === 0 ? (
        <EmptyState compact title={`No sign-ups in the last ${weeks} weeks`} />
      ) : (
        <Box className="sw-tables">
          <WeeklyTable title="Where they came from" first="Channel" rows={tables.channels} weeks={data} total={tables.total} />
          <WeeklyTable title="Where they landed" first="Landing page" rows={tables.landingPages} weeks={data} total={tables.total} />
          <WeeklyTable title="How they signed up" first="Method" rows={tables.methods} weeks={data} total={tables.total} />
        </Box>
      )}
    </Panel>
  );
};

export default SignupsByWeek;
